/* eslint-disable react/require-default-props */
/* eslint-disable import/no-extraneous-dependencies */
import React, {
  useContext, useEffect, useState, useRef,
} from 'react';
import { createGlobalStyle } from 'styled-components';

import PropTypes from 'prop-types';
import { Route, Routes, useLocation } from 'react-router-dom';
import window from 'global/window';
import { AuthClient } from '@adsk/identity-web-sdk';
import deepmerge from 'deepmerge';
import { I18nextProvider } from 'react-i18next';
import { jwtDecode } from 'jwt-decode';
import {
  ENVIRONMENT,
  MAINTENANCE_MODE,
  PKCE_CLIENT_ID,
  PKCE_ENV,
  styleVariables,
} from './app/common/constants';
import { defaultRoutes, maintenanceRoutes } from './app/common/routerConfig';
import handleUnauthRedirect from './app/common/handleUnauthRedirect';
import EnvContext from './app/common/envContext';
import { LoginClickHandler } from './app/common/login';
import { getCurrentPathName, getEnvironmentConstant } from './app/common/utilities';
import authLib from './app/common/pkce';
import i18n from './i18n';
import generateLocalesArray from './app/utils/generateLanguageSelectorUrls';
import useBackButton from './app/common/useBackButton';

function RouterApp(props) {
  const location = useLocation();
  const localeRef = useRef(props?.localeKey || null);
  const [currentPathName, setCurrentPathName] = useState('');
  const envContext = useContext(EnvContext);
  const componentLoader = envContext.COMPONENT_LOADER;
  const env = envContext[ENVIRONMENT];
  const inMaintenanceMode = envContext[MAINTENANCE_MODE];
  const routes = inMaintenanceMode ? maintenanceRoutes : defaultRoutes;
  let authValidationInterval;
  const currentPath = useBackButton();

  useEffect(() => {
    if (localeRef.current && window?.location?.hostname && window.location.hostname.endsWith('.com.cn')) {
      const newUrl = getCurrentPathName(location);
      window.history.pushState({ path: newUrl }, '', newUrl);
      localeRef.current = null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props, location]);

  useEffect(() => {
    if (!window.location.hash && window.scrollTo !== undefined && currentPathName !== location.pathname) {
      setCurrentPathName(location.pathname);
      setTimeout(() => window.scrollTo(0, 0), 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  authLib.init(
    new AuthClient({
      client_id: getEnvironmentConstant({ env, key: PKCE_CLIENT_ID }),
      redirect_uri: `${window?.location?.origin}/support/partners/auth`,
      useRefreshTokens: true,
      env: getEnvironmentConstant({ env, key: PKCE_ENV }),
      scope: 'data:read openid user-profile:read',
    }),
  );

  const fetchUserData = async () => {
    const auth = authLib.authClient;

    try {
      const { accessToken, idToken } = await new Promise((resolve, reject) => {
        auth.getAccessTokenSilently()
          .catch((e) => {
            reject(e);
            return null;
          })
          .then((at) => {
            auth.getIDTokenSilently()
              .catch((e) => {
                reject(e);
                return null;
              })
              .then((it) => {
                resolve({ accessToken: at, idToken: it });
              });
          });
      });

      // Update login state for UH
      window.headerConfig.accessToken = accessToken;
      const evt = new CustomEvent('SSO_SIGNED_IN', { detail: { at: accessToken } });
      window.dispatchEvent(evt);

      const decodedIdToken = jwtDecode(idToken);
      const mappedData = {
        userid: decodedIdToken.userid,
        oa2Token: accessToken,
      };

      return Promise.resolve(mappedData);
    } catch (e) {
      if (!e.toString().includes('Login required')) {
        console.error('PKCE ERROR w/ getTokenSilently:', e);
      }
      return Promise.reject(new Error('No PKCE Login'));
    }
  };

  const validateSignIn = () => {
    // Get User State
    fetchUserData(env).then(
      () => {
        console.info('user data fetched');
      },
    );
  };

  const componentsData = () => ({
    meMenu: {
      onSignIn: () => {
        LoginClickHandler();
      },
      onSignOut: () => {
        authLib.authClient
          .logout({
            post_logout_redirect_uri: window.location.href,
          })
          .then(() => {
            handleUnauthRedirect({ env });
          });
      },
    },
    cart: {
      useMinicart: false,
      count: 0,
    },
    languageSelector: {
      defaultUrl: `${window.location.origin}/support/partners`,
    },
  });

  const reRenderHeader = () => {
    if (window.headerConfig) {
      const components = componentsData();
      window.headerConfig.components = deepmerge(
        window.headerConfig.components,
        components,
      );
      window.headerConfig.components.languageSelector.localeUrlMapping = generateLocalesArray();
      if (window.adsk && window.adsk.components && window.adsk.components.universalHeader) {
        window?.adsk?.components?.universalHeader?.reRender(window.headerConfig);
      }
    }
  };

  useEffect(() => {
    validateSignIn();

    if (authValidationInterval) {
      clearInterval(authValidationInterval);
    }
    authValidationInterval = setInterval(validateSignIn, 3000000);
  }, []);

  useEffect(() => {
    reRenderHeader();
  }, [location]);

  return (
    <>
      <I18nextProvider i18n={i18n}>
        <GlobalStyles />
        <Routes>
          {routes.map((route) => {
            const Comp = componentLoader[route.componentKey];
            return (
              <Route
                key={route.path}
                path={route.path}
                element={
                  (
                    <Comp
                      currentPath={currentPath}
                      store={props.store}
                    />
                  )
                }
              />
            );
          })}
        </Routes>
      </I18nextProvider>
    </>
  );
}

RouterApp.propTypes = {
  store: PropTypes.shape({}),
  localeKey: PropTypes.string,
};

RouterApp.defaultProps = {
  store: {},
  localeKey: '',
};
const GlobalStyles = createGlobalStyle`
  * {
    padding: 0px;
    margin: 0px;
    font-family: 'ArtifaktElement';
  }

  .page-container{
      margin: 0px auto;
      padding: 0px 1em;
      max-width: 100%;
      @media (min-width: 1340px) {
          max-width: calc(1340px - 2em);
          margin: 0px auto;
          padding: 0px 1em;
      }
  }

  // Override for bad box model changes in :focus state for Select component
  li.MuiMenuItem-root {
    margin: 0 0.125rem !important;
    padding: 0.5rem 0.75rem !important;
  }

  .container .universal-footer .page-container {
    max-width: calc(1340px - 2em);
    margin: 0 auto;
    padding: 0 1em;

  }
  .container .universal-footer .inner-footer{
    border:none;
  }
  .container .universal-footer .mega-footer-column__title p {
    margin: 0;
  }

  html {
    font-size: ${styleVariables.primitivesFontSize['3']} !important;
    -webkit-text-size-adjust: 100%;
    .uh-container-wrapper .uh-container-section-wrapper.uh-mobile-pn-open .uh-container{
      z-index: 1302;
    }

    #lo-uh-container {
      background: ${styleVariables.inkOnBackground};
      z-index: 1302;
      height: 98px;
      position: relative;

      .uh-container-wrapper .uh-container-section-wrapper {
        margin: 0;
        width: auto;
      }
    }

    #lo-uf-container {
      min-height: 416px;
    }


    @media only screen and (max-width: 1040px) {
      #lo-uh-container {
        height: 57px;
      }

      #lo-uf-container {
        min-height: 357px;
      }

    }

    .maxWidth {
      max-width: 1220px;
      margin: 0 auto;
    }

    .rightPadding {
      padding-right: 15px;
    }

    .leftPadding {
      padding-left: 15px;
    }

    @media only screen and (max-width: 1000px) {
      .rightPadding {
        padding-right: 16px;
      }

      .leftPadding {
        padding-left: 16px;
      }
    }

    .MuiAutocomplete-popper .MuiListSubheader-sticky {
      background-color: ${styleVariables.background} !important;
    }
  }

  div.MuiTooltip-tooltip {
    color: ${styleVariables.background};
    border-radius: 0px;
    font-size: ${styleVariables.primitivesFontSize['1']};
    max-width: 135px;
  }
  div.MuiTooltip-popper {
    margin-top:-10px !important;
    @media (max-width: 991px) {
      margin-top:-22px !important;
    }
  }
`;

export default RouterApp;
